import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getErrorMessage } from "../../helpers/get-error-message";
import { UserRowType, RequestUsersParams, InviteUserParams } from "./types";
import { AppThunk } from "../../store";
import { getCustomersApi } from "../../ancillaries/api";
import {
  SupplierType,
  CustomerType,
  RequestCustomersParams,
} from "../../ancillaries/types";

import {
  getUsersApi,
  inviteUserApi,
  resendInvitationApi,
} from "../../auth/api";

import { deleteUserApi, editUserRoleApi, restoreUserApi } from "./api";

type usersType = {
  usersState: {
    items: UserRowType[] | null;
    total: number;
    loading: boolean;
    params?: RequestUsersParams;
  };
  invite: {
    success: string | null;
    error: string | null;
  };
  error: string | null;
  success: string | null;
  selectedUser: UserRowType | null;
  suppliers?: SupplierType[] | null;
  customers?: CustomerType[] | null;
  customersTotal?: number;
};

const initialState: usersType = {
  usersState: {
    items: null,
    total: 0,
    loading: false,
  },
  success: null,
  error: null,
  selectedUser: null,
  suppliers: null,
  customers: null,
  customersTotal: 0,
  invite: {
    success: null,
    error: null,
  },
};

const users = createSlice({
  name: "users",
  initialState: initialState,
  reducers: {
    getUsersStart(state: usersType) {
      state.usersState.loading = true;
      state.error = null;
    },
    getUsersSuccess(
      state: usersType,
      action: PayloadAction<{
        items: UserRowType[];
        total: number;
        params: RequestUsersParams;
      }>,
    ) {
      state.usersState.items = action.payload.items;
      state.usersState.total = action.payload.total;
      state.usersState.params = action.payload.params;
      state.usersState.loading = false;
      state.error = null;
    },
    getSuppliersSuccess(
      state: usersType,
      action: PayloadAction<{
        items: SupplierType[];
      }>,
    ) {
      state.suppliers = action.payload.items;
    },
    getCustomersSuccess(
      state: usersType,
      action: PayloadAction<{
        items: CustomerType[];
        total: number;
      }>,
    ) {
      state.customers = action.payload.items;
      state.customersTotal = action.payload.total;
    },
    getUsersFailure(state: usersType, action: PayloadAction<string>) {
      state.usersState.loading = false;
      state.error = action.payload;
    },
    inviteUserFailure(state: usersType, action: PayloadAction<string>) {
      state.invite.error = action.payload;
    },
    setSelectedUser(state: usersType, action: PayloadAction<number | null>) {
      state.selectedUser =
        state.usersState.items?.find((user) => user.id === action.payload) ||
        null;
    },
    hideWarning(state: usersType) {
      state.error = null;
      state.success = null;
    },
    hideInviteWarning(state: usersType) {
      state.invite.error = null;
      state.invite.success = null;
    },
    inviteUserSuccess(state: usersType) {
      state.invite.success = "Invitation was sent successfully";
    },
    editUser(
      state: usersType,
      action: PayloadAction<{
        userId: number;
        role: string;
        office_ids?: number[];
        warehouse_ids?: number[];
        offices?: { id: number; name: string }[];
        warehouses?: { id: number; name: string }[];
      }>,
    ) {
      const { userId, role, offices, warehouses } = action.payload;
      state.usersState.items?.forEach((user) => {
        if (user.id === userId) {
          user.role = role;
          if (offices !== undefined) {
            user.offices = offices;
          }
          if (warehouses !== undefined) {
            user.warehouses = warehouses;
          }
        }
      });
      if (state.selectedUser?.id === userId) {
        state.selectedUser.role = role;
        if (offices !== undefined) {
          state.selectedUser.offices = offices;
        }
        if (warehouses !== undefined) {
          state.selectedUser.warehouses = warehouses;
        }
      }
      state.success = "The changes were saved successfully";
      state.usersState.loading = false;
    },
    removeUser(state: usersType, action: PayloadAction<number>) {
      const userId = action.payload;
      if (state.usersState.items) {
        state.usersState.items = state.usersState.items.filter(
          (user) => user.id !== userId,
        );
        state.usersState.total = Math.max(0, state.usersState.total - 1);
      }
      if (state.selectedUser?.id === userId) {
        state.selectedUser = null;
      }
      state.success = "User removed successfully";
    },
  },
});

export const {
  getUsersStart,
  getUsersSuccess,
  getUsersFailure,
  getSuppliersSuccess,
  getCustomersSuccess,
  inviteUserFailure,
  inviteUserSuccess,
  setSelectedUser,
  hideInviteWarning,
  hideWarning,
  editUser,
  removeUser,
} = users.actions;

export default users.reducer;

export const fetchUsers =
  (params: RequestUsersParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getUsersStart());
      const { items, total } = await getUsersApi(params);
      dispatch(getUsersSuccess({ items, total, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getUsersFailure(error));
    }
  };

export const deleteUser =
  (userId: number): AppThunk =>
  async (dispatch) => {
    try {
      await deleteUserApi(userId);
      dispatch(removeUser(userId));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getUsersFailure(error));
    }
  };

export const restoreUser =
  (userId: number): AppThunk =>
  async (dispatch, getState) => {
    try {
      await restoreUserApi(userId);
      const state = getState();
      const currentParams = state.users.usersState.params;
      if (currentParams) {
        dispatch(fetchUsers(currentParams));
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getUsersFailure(error));
    }
  };

export const updateUser =
  (params: {
    userId: number;
    role: string;
    first_name: string;
    last_name: string;
    office_ids?: number[];
    warehouse_ids?: number[];
  }): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(getUsersStart());
      await editUserRoleApi(params);

      const state = getState();
      const offices = (state.ancillaries.data?.offices?.items || []) as Array<{
        id: number;
        name: string;
      }>;
      const warehouses = (state.ancillaries.data?.warehouses?.items ||
        []) as Array<{
        id: number;
        name: string;
      }>;

      const officeObjects = params.office_ids
        ? params.office_ids
            .map((id) => offices.find((office) => office.id === id))
            .filter(
              (office): office is { id: number; name: string } =>
                office !== undefined,
            )
        : undefined;

      const warehouseObjects = params.warehouse_ids
        ? params.warehouse_ids
            .map((id) => warehouses.find((warehouse) => warehouse.id === id))
            .filter(
              (warehouse): warehouse is { id: number; name: string } =>
                warehouse !== undefined,
            )
        : undefined;

      dispatch(
        editUser({
          userId: params.userId,
          role: params.role,
          office_ids: params.office_ids,
          warehouse_ids: params.warehouse_ids,
          offices: officeObjects,
          warehouses: warehouseObjects,
        }),
      );

      const currentParams = state.users.usersState.params;
      if (currentParams) {
        dispatch(fetchUsers(currentParams));
      }
    } catch (err) {
      const error = (err as any)?.response?.data?.detail;
      dispatch(getUsersFailure(error));
    }
  };

export const fetchCustomers =
  (params?: RequestCustomersParams): AppThunk =>
  async (dispatch) => {
    try {
      const { items, total } = await getCustomersApi(params);
      dispatch(getCustomersSuccess({ items, total }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getUsersFailure(error));
    }
  };

export const inviteUser =
  (params: InviteUserParams): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(hideInviteWarning());
      await inviteUserApi(params);
      dispatch(inviteUserSuccess());
      const state = getState();
      const currentParams = state.users.usersState.params;
      if (currentParams) {
        dispatch(fetchUsers(currentParams));
      } else {
        dispatch(fetchUsers({ page: 1, size: 10 }));
      }
    } catch (err) {
      dispatch(inviteUserFailure(getErrorMessage(err)));
    }
  };

export const resendInvitation =
  (params: { user_id: number }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(hideInviteWarning());
      await resendInvitationApi(params);
      dispatch(inviteUserSuccess());
    } catch (err) {
      dispatch(inviteUserFailure(getErrorMessage(err)));
    }
  };
